import { FC, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Divider as _Divider, Drawer, IconButton, List, ListItem, Tooltip, withStyles } from '@material-ui/core';
import { useQuery, useReactiveVar } from '@apollo/client';
import SideBarNav, { Page } from './SideBarNav';
import { TOPBAR_HEIGHT } from '../TopBar';
import theme from '../../../../theme';
import { context as ConfigContext } from '../../../context/config';
import { COLOR_SIDEMENU_BACKGROUND } from '../../../../theme/colors';
import SalesIcon from '../../primitive/Icons/SalesIcon';
import EngineIcon from '../../primitive/Icons/EngineIcon';
import VideoIcon from '../../primitive/Icons/VideoIcon';
import AccountConsoleIcon from '../../primitive/Icons/AccountConsoleIcon';
import { lastAccessedOrganizationVar, currentUserVar } from '../../../../cache';
import {
  GetCurrentK2UserDocument,
  GetCurrentK2UserQuery,
  GetCurrentK2UserQueryVariables,
} from '../../../../generated/graphql';

type Props = {
  onChangePage: (queryParams: string) => void;
  className?: string;
};

const SideBar: FC<Props> = ({ onChangePage, className }) => {
  const { id: org } = useReactiveVar(lastAccessedOrganizationVar) ?? { id: null };
  const { email } = useReactiveVar(currentUserVar) ?? { email: null };
  const config = useContext(ConfigContext);
  const [usersCurrent, setUsersCurrent] = useState<any>();

  //GetCurrentK2UsersCurrent
  useQuery<GetCurrentK2UserQuery, GetCurrentK2UserQueryVariables>(GetCurrentK2UserDocument, {
    skip: usersCurrent,
    context: { clientName: 'planbcd' },
    onCompleted: (data: GetCurrentK2UserQuery) => {
      setUsersCurrent(data?.user);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const pages = useMemo<Array<Page | undefined>>(
    () =>
      org
        ? [
            {
              title: 'サイト全体',
            },
            {
              title: 'サイト分析',
              subTitle: 'Site Analytics',
              href: '/engine/site-analytics',
              category: ['/engine/site-analytics'],
            },
            {
              title: '施策効果レポート',
              subTitle: 'Lift Report',
              href: '/engine/liftreport',
              category: ['/engine/liftreport'],
            },
            {
              title: '検索流入分析(β)',
              href: '/engine/site-search-analytics' + `?org=${org}`,
              category: ['/engine/site-search-analytics'],
            },
            {
              title: 'リンク先読み',
              subTitle: 'Link Prefetch',
              href: '/engine/link-prefetch' + `?org=${org}`,
              category: ['/engine/link-prefetch'],
            },
            // {
            //   title: 'カスタムレポート',
            //   href: '/customreport',
            // },
            {
              title: '施策',
            },
            {
              title: 'エクスペリエンス',
              href: `/engine/users/${org}/experiments`,
              category: [
                /\/engine\/users\/[0-9]+?\/experiments/,
                /\/engine\/experiments\/[0-9]+?/,
                /\/engine\/experiments\//,
              ],
            },
            email?.includes('@kaizenplatform.com')
              ? {
                  title: 'エクスペリエンス(社内用)',
                  href: `/engine/experiments_b`,
                  category: [
                    /\/engine\/users_b\/[0-9]+?\/experiments/,
                    /\/engine\/experiments_b\/[0-9]+?/,
                    /\/engine\/experiments_b/,
                  ],
                }
              : undefined,
            {
              title: '施策の評価・振り返り',
              subTitle: 'Experience Portfolio',
              href: '/engine/exp-portfolio' + `?org=${org}`,
              category: ['/engine/exp-portfolio'],
            },
            {
              title: '効果のモニタリング',
              subTitle: 'Round Monitor',
              link: config.ROUND_REPORT,
            },
            {
              title: 'サイト診断',
              subTitle: 'Site Dock',
              href: '/engine/sitedock-v2' + `?org=${org}`,
              category: ['/engine/sitedock-v2'],
            },
            {
              title: '募集中オファー',
              href: '/engine/my_experiments' + `?org=${org}`,
              category: ['/engine/my_experiments'],
            },
            {
              title: 'コメントボード',
              href: '/engine/comment-board' + `?org=${org}`,
              category: ['/engine/comment-board'],
            },
            {
              title: '設定',
            },
            {
              title: 'ゴール',
              href: '/engine/organizations/' + (org ?? '') + '/goals',
              category: [/\/engine\/organizations\/[0-9]+?\/goals/, /\/engine\/goals\/[0-9]+?\/edit/],
            },
            email?.includes('@kaizenplatform.com')
              ? {
                  title: 'ゴール(社内用)',
                  href: '/engine/goal-setting/',
                  category: [/\/engine\/goal-setting/, /\/engine\/goal-setting\/sample/],
                }
              : undefined,
            {
              title: 'JavaScriptコード',
              href: '/engine/js_code' + `?org=${org}`,
              category: ['engine/js_code'],
            },
          ]
        : [
            {
              title: '施策',
            },
            {
              title: '募集中オファー',
              href: '/engine/my_experiments',
              category: [
                /\/engine\/my_experiments/,
                /\/engine\/users\/[0-9]+?\/experiments/,
                /\/engine\/experiments\/[0-9]+?/,
                /\/engine\/experiments/,
              ],
            },
            {
              title: 'コメントボード(β)',
              href: '/engine/comment-board',
              category: ['/engine/comment-board'],
            },
          ],
    [org, usersCurrent],
  );

  return (
    <Static className={className} anchor="left" variant="permanent">
      <div
        style={{
          borderRight: 'solid 1px #E6E7E8',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          background: '#FFFFFF',
          zIndex: 1,
          width: 62,
          transition: 'all 300ms 0s ease',
          overflow: 'hidden',
        }}
      >
        <StyledList>
          <ListItem style={{ height: 72 }} />
          <ListItem
            style={{
              height: 48,
              justifyContent: 'center',
              margin: '16px 0px',
            }}
          >
            <Tooltip title={'動画活用'} placement="right">
              <IconButton
                href={`${config.SALES_URL}sales/`}
                style={{
                  borderRadius: 8,
                }}
              >
                <SalesIcon />
              </IconButton>
            </Tooltip>
          </ListItem>
          <ListItem
            style={{
              height: 48,
              justifyContent: 'center',
              margin: '16px 0px',
            }}
          >
            <Tooltip title={'サイト改善'} placement="right">
              <IconButton
                href="#"
                style={{
                  borderRadius: 8,
                  backgroundColor: '#F5F6F7',
                }}
              >
                <EngineIcon />
              </IconButton>
            </Tooltip>
          </ListItem>
          <ListItem
            style={{
              height: 48,
              justifyContent: 'center',
              margin: '16px 0px',
            }}
          >
            <Tooltip title={'動画制作'} placement="right">
              <IconButton
                href={`${config.SALES_URL}ad/`}
                style={{
                  borderRadius: 8,
                }}
              >
                <VideoIcon />
              </IconButton>
            </Tooltip>
          </ListItem>
        </StyledList>
        <StyledList>
          <ListItem
            style={{
              height: 48,
              justifyContent: 'center',
              margin: '16px 0px',
            }}
          >
            <Tooltip title={'アカウント設定'} placement="right">
              <IconButton
                href={config.ID_WEB_URL}
                style={{
                  borderRadius: 8,
                }}
              >
                <AccountConsoleIcon />
              </IconButton>
            </Tooltip>
          </ListItem>
        </StyledList>
      </div>
      <div>
        <Nav>
          <SideBarNav onChangePage={onChangePage} pages={pages.filter((v): v is Page => v != null)} />
        </Nav>
      </div>
    </Static>
  );
};

const Nav = styled('nav')({
  display: 'flex',
  borderRight: 'solid 1px #E6E7E8',
  flexDirection: 'column',
  width: 240,
  height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
  marginTop: TOPBAR_HEIGHT,
  padding: 16,
  backgroundColor: theme.palette.common.white,
  zIndex: 100,
});

const StyledList = withStyles({
  root: {
    width: 62,
    padding: 0,
  },
})(List);

const Static = withStyles({
  root: {
    width: 302,
    flexShrink: 0,
  },
  paperAnchorDockedLeft: {
    borderRight: 'none',
  },
  paper: {
    flexDirection: 'row',
    backgroundColor: COLOR_SIDEMENU_BACKGROUND,
  },
})(Drawer);

export default SideBar;
